import { useEffect, useState } from 'react';
import { registerModalBackHandler } from '../../lib/native';
import type { EventLibraryItem } from '../../types';

interface LibraryPickerModalProps {
  items: EventLibraryItem[];
  categories: { slug: string; name: string }[];
  dayLabel: string;
  onPick: (item: EventLibraryItem) => void;
  onClose: () => void;
}

/**
 * Browse the host's saved places and add them straight into the active day.
 * Stays open so several places can be added in one go; each added item shows a
 * brief confirmation. No staging step.
 */
export function LibraryPickerModal({ items, categories, dayLabel, onPick, onClose }: LibraryPickerModalProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');
  const [addedIds, setAddedIds] = useState<string[]>([]);

  const catName = (slug: string) => categories.find((c) => c.slug === slug)?.name ?? slug;

  const filtered = items.filter((i) => {
    if (filter && i.category_slug !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return i.name.toLowerCase().includes(q) || catName(i.category_slug).toLowerCase().includes(q);
    }
    return true;
  });

  function handlePick(item: EventLibraryItem) {
    onPick(item);
    setAddedIds((prev) => [...prev, item.id]);
  }

  // Android hardware back button closes this modal instead of navigating
  // the route underneath it.
  useEffect(() => registerModalBackHandler(onClose), [onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center" onClick={onClose}>
      <div
        className="bg-app-card md:border md:border-app-border rounded-t-3xl md:rounded-2xl w-full max-w-lg max-h-dvh-92 overflow-y-auto pb-safe-or-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-app-card z-10 px-5 pt-5 pb-3 border-b border-app-border">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-app-fg">From library</h3>
              <p className="text-xs text-app-muted truncate">Adding to {dayLabel}</p>
            </div>
            <button onClick={onClose} className="bg-gold text-ink text-sm font-semibold px-4 py-2 rounded-full hover:brightness-105 transition-all shrink-0">
              Done
            </button>
          </div>

          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your library…"
            className="w-full mt-3 rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm text-app-fg placeholder:text-app-muted focus:outline-none" />

          {/* Category filter */}
          <div className="flex gap-2 flex-wrap mt-2">
            <button onClick={() => setFilter('')}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${!filter ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}>
              All
            </button>
            {categories.map((cat) => {
              const count = items.filter((i) => i.category_slug === cat.slug).length;
              if (count === 0) return null;
              return (
                <button key={cat.slug} onClick={() => setFilter(filter === cat.slug ? '' : cat.slug)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${filter === cat.slug ? 'bg-app-fg/10 text-app-fg' : 'text-app-muted hover:text-app-fg'}`}>
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        <div className="px-5 py-4 flex flex-col gap-2">
          {filtered.length === 0 ? (
            <p className="text-sm text-app-muted text-center py-8">
              {items.length === 0 ? 'Your library is empty. Save places from an event, or add them in Event Library.' : 'No matching places.'}
            </p>
          ) : (
            filtered.map((item) => {
              const added = addedIds.includes(item.id);
              return (
                <button key={item.id} onClick={() => handlePick(item)}
                  className="flex items-center gap-3 w-full text-left bg-app-bg border border-app-border rounded-xl p-2.5 hover:border-app-fg/20 transition-colors">
                  {item.photo_url ? (
                    <img src={item.photo_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" loading="lazy" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-app-border/50 flex items-center justify-center shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-app-muted/40">
                        <path fillRule="evenodd" d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909-3.22-3.22a.75.75 0 00-1.06 0L2.5 11.06zm12.25-3.31a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-app-fg truncate">{item.name}</p>
                    <p className="text-xs text-app-muted">{catName(item.category_slug)}</p>
                  </div>
                  {added ? (
                    <span className="flex items-center gap-1 text-xs text-success shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                      </svg>
                      Added
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-app-muted shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                      </svg>
                      Add
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default LibraryPickerModal;
